import React, { useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { db } from '../../../../utils/firebase';
import { collection, query, where, getDocs, writeBatch, doc, Timestamp, getDoc } from 'firebase/firestore';
import Button from '../../../../components/ui/Button';
import { computeSalaryDeduction, parseSalaryTaxSettings, buildSalaryPointHistoryFields } from '../../../../utils/salaryTax';

interface ManualPointDistributionProps {
  period: 'daily' | 'weekly' | 'monthly';
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const ManualPointDistribution: React.FC<ManualPointDistributionProps> = ({
  period,
  onSuccess,
  onError
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const teacher = currentUser as any;

  const handleManualDistribution = async () => {
    if (!teacher?.schoolId || !teacher?.classId) {
      setError('학교 ID 또는 클래스 ID가 없습니다.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const approvalsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointApprovals`);
      const approvalsQuery = query(
        approvalsRef,
        where('isApproved', '==', true),
        where('salaryPeriod', '==', period)
      );
      
      const approvalsSnapshot = await getDocs(approvalsQuery);
      
      if (approvalsSnapshot.empty) {
        setError('처리할 승인 요청이 없습니다.');
        return;
      }

      const taxSnap = await getDoc(
        doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/salaryTaxSettings`, 'default')
      );
      const taxSettings = parseSalaryTaxSettings(taxSnap.data() as Record<string, unknown> | undefined);

      const batch = writeBatch(db);
      let processedCount = 0;

      for (const approvalDoc of approvalsSnapshot.docs) {
        const approval = approvalDoc.data();
        const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students/${approval.uid}`);
        const studentDoc = await getDoc(studentRef);
        
        if (studentDoc.exists()) {
          const studentData = studentDoc.data();
          const currentPoints = studentData?.points || 0;
          const role = approval.roleId;
          
          // classRoles 컬렉션에서 해당 역할이 포함된 문서를 찾습니다
          const roleDocs = await getDocs(collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/classRoles`));
          let roleData = null;
          
          for (const doc of roleDocs.docs) {
            const data = doc.data();
            if (data.id === role) {
              roleData = data;
              break;
            }
          }
          
          if (roleData) {
            const gross = roleData.salary || 0;
            const deduction = computeSalaryDeduction(gross, taxSettings);
            
            // 학생의 포인트 업데이트
            batch.update(studentRef, {
              points: currentPoints + deduction.net
            });
            
            // 포인트 지급 기록 추가
            const historyRef = doc(collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointHistories`));
            batch.set(historyRef, {
              ...buildSalaryPointHistoryFields(approval.uid, deduction, `${roleData.name} 수동 지급`, period),
              date: Timestamp.now(),
              createdAt: Timestamp.now()
            });
            
            // 다음 지급일 설정
            const now = new Date();
            let nextTargetDate = new Date();
            
            if (period === 'daily') {
              nextTargetDate.setDate(now.getDate() + 1);
              nextTargetDate.setHours(23, 59, 0, 0);
            } else if (period === 'weekly') {
              const daysUntilNextSunday = 7 - now.getDay();
              nextTargetDate.setDate(now.getDate() + daysUntilNextSunday);
              nextTargetDate.setHours(23, 59, 0, 0);
            } else if (period === 'monthly') {
              nextTargetDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
              nextTargetDate.setHours(23, 59, 0, 0);
            }
            
            // 새로운 승인 요청 생성
            const newApprovalRef = doc(collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointApprovals`));
            batch.set(newApprovalRef, {
              uid: approval.uid,
              roleId: approval.roleId,
              targetDate: Timestamp.fromDate(nextTargetDate),
              salaryPeriod: period,
              isApproved: false,
              pointAutoPay: approval.pointAutoPay || false,
              requiresTeacherApproval: approval.requiresTeacherApproval || false,
              requiresPointManagerApproval: approval.requiresPointManagerApproval || false,
              requiresBothApprovals: approval.requiresBothApprovals || false,
              createdAt: Timestamp.now()
            });
            
            // 기존 승인 요청 삭제
            batch.delete(approvalDoc.ref);
            
            processedCount++;
          }
        }
      }
      
      if (processedCount > 0) {
        await batch.commit();
        console.log(`수동 포인트 지급 완료: ${processedCount}개 처리`);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError('처리할 승인 요청이 없습니다.');
      }
    } catch (err) {
      console.error('수동 지급 중 오류:', err);
      const errorMessage = err instanceof Error ? err.message : '수동 지급 중 오류가 발생했습니다.';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const pxToRem = (value: number) => `${value / 16}rem`;

  return (
    <>
      {error && (
        <div style={{
          width: '100%',
          padding: pxToRem(12),
          background: 'var(--Color-status-negative, #FF3838)',
          color: 'white',
          borderRadius: pxToRem(8),
          fontSize: pxToRem(14),
          fontFamily: 'Pretendard, sans-serif',
          marginBottom: pxToRem(16)
        }}>
          {error}
        </div>
      )}
      <Button
        width={pxToRem(118)}
        height={pxToRem(40)}
        variant="submit"
        onClick={handleManualDistribution}
        disabled={loading}
      >
        {loading ? '처리 중...' : '포인트 일괄 지급'}
      </Button>
    </>
  );
};

export default ManualPointDistribution;
